import { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClinic } from '../context/ClinicContext';

export default function Login() {
  const { login, user, loading } = useAuth();
  const { clinic } = useClinic();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to={location.state?.from || '/dashboard'} replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(form.username.trim(), form.password);
      navigate(location.state?.from || '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5} xl={4}>
            <Card className="login-card">
              <Card.Body className="p-4 p-lg-5">
                <div className="text-center mb-4">
                  <div className="login-logo">
                    <i className="bi bi-heart-pulse-fill" />
                  </div>
                  <h4 className="fw-bold mb-1">{clinic.clinicName}</h4>
                  <div className="text-muted small">{clinic.tagline || 'Clinic Management System'}</div>
                </div>

                {error && (
                  <Alert variant="danger" className="py-2 small">
                    <i className="bi bi-exclamation-triangle me-2" />
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      autoFocus
                      required
                      size="lg"
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      placeholder="doctor or reception"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Password</Form.Label>
                    <InputGroup size="lg">
                      <Form.Control
                        required
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="Enter your password"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                        title={showPassword ? 'Hide password' : 'Show password'}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  <Button type="submit" size="lg" className="w-100 btn-clinic" disabled={busy}>
                    {busy ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-box-arrow-in-right me-2" />
                        Sign In
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            <div className="text-center text-white-50 small mt-3">
              Trouble signing in? The two logins are set in the server's <code className="text-white-50">.env</code>{' '}
              file. Edit it there and restart the server.
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
